export type MissionId = "mow" | "snow" | "plant" | "cargo" | "boat" | "finale";

export type RoverMissionId = Exclude<MissionId, "finale">;

export type Mission = {
  id: MissionId;
  number: string;
  title: string;
  rover: string;
  kicker: string;
  description: string;
  objective: string;
  metric: string;
  color: string;
  accent: string;
  ground: string;
  sky: string;
  maxSpeed: number;
  acceleration: number;
  steering: number;
  parTime: number;
  total: number;
};

export const missions: Mission[] = [
  {
    id: "mow",
    number: "01",
    title: "Идеальный контур",
    rover: "MOW·01",
    kicker: "Газонокосилка",
    description:
      "Верните лужайке идеальную геометрию. Пройдите всю площадь и не заденьте клумбы.",
    objective: "Скосить 100% высокой травы",
    metric: "покрытие",
    color: "#8ef06c",
    accent: "#dfff73",
    ground: "#22402e",
    sky: "#a5d8d0",
    maxSpeed: 8.4,
    acceleration: 9,
    steering: 1.85,
    parTime: 70,
    total: 48,
  },
  {
    id: "snow",
    number: "02",
    title: "Белый коридор",
    rover: "SNOW·02",
    kicker: "Снегоуборщик",
    description:
      "Ночная смена после снегопада. Освободите технический проезд от снежных наносов.",
    objective: "Расчистить сервисный маршрут",
    metric: "расчищено",
    color: "#9ee8ff",
    accent: "#ecfbff",
    ground: "#bed5dc",
    sky: "#18304c",
    maxSpeed: 7.2,
    acceleration: 7.5,
    steering: 1.55,
    parTime: 75,
    total: 34,
  },
  {
    id: "plant",
    number: "03",
    title: "Ровные ряды",
    rover: "FIELD·03",
    kicker: "Картофелесажалка",
    description:
      "Заложите демонстрационное поле. Следуйте рядам по порядку и держите ровную линию.",
    objective: "Засадить восемь рядов поля",
    metric: "засажено",
    color: "#ffb65d",
    accent: "#ffe49a",
    ground: "#594630",
    sky: "#f2b27a",
    maxSpeed: 6.4,
    acceleration: 6.4,
    steering: 1.35,
    parTime: 90,
    total: 40,
  },
  {
    id: "cargo",
    number: "04",
    title: "Логистическая смена",
    rover: "HAUL·04",
    kicker: "Грузовой мувер",
    description:
      "Соберите три контейнера на терминалах и доставьте комплект на склад. Для погрузки нужно точно остановиться на платформе.",
    objective: "Забрать 3 контейнера и доставить на склад",
    metric: "станции",
    color: "#f5dd46",
    accent: "#fff7b0",
    ground: "#3b3d38",
    sky: "#8da69f",
    maxSpeed: 9.6,
    acceleration: 6.8,
    steering: 1.18,
    parTime: 75,
    total: 4,
  },
  {
    id: "boat",
    number: "05",
    title: "К воде",
    rover: "MARINE·05",
    kicker: "Мувер для катера",
    description:
      "Проведите длинный прицеп по серпантину, развернитесь перед водой и подайте катер на рампу задним ходом.",
    objective: "Пройти маршрут и подать катер задним ходом",
    metric: "до воды",
    color: "#5ec9ff",
    accent: "#b9efff",
    ground: "#6e7666",
    sky: "#7ccbe4",
    maxSpeed: 8.8,
    acceleration: 6.2,
    steering: 1.02,
    parTime: 85,
    total: 10,
  },
];

export const finaleMission: Mission = {
  id: "finale",
  number: "06",
  title: "Испытательная смена",
  rover: "MULTI·06",
  kicker: "Финальный полигон",
  description:
    "Три коротких задания без перерыва: обработайте сектор, соберите оборудование и завершите смену точной подачей к воде.",
  objective: "Пройти три этапа на разных роверах",
  metric: "этапы",
  color: "#f3ff66",
  accent: "#ffffff",
  ground: "#344039",
  sky: "#8fc4bd",
  maxSpeed: 9.2,
  acceleration: 7.2,
  steering: 1.35,
  parTime: 145,
  total: 15,
};

export const missionCatalogue = [...missions, finaleMission];

export const getMission = (id: MissionId) =>
  missionCatalogue.find((mission) => mission.id === id) ?? missions[0];

export const getFinaleRover = (completed: number): RoverMissionId => {
  if (completed < 5) return "mow";
  if (completed < 9) return "cargo";
  return "boat";
};

export type StageInfo = {
  key: string;
  label: string;
  title: string;
  objective: string;
  alert?: boolean;
};

export const getStageInfo = (
  missionId: MissionId,
  completed: number,
): StageInfo => {
  if (missionId === "mow") {
    return completed < 24
      ? {
          key: "mow-main",
          label: "ЭТАП 1 / 2",
          title: "Чистый контур",
          objective: "Обработайте первую половину газона",
        }
      : {
          key: "mow-water",
          label: "УСЛОВИЯ ИЗМЕНИЛИСЬ",
          title: "Система полива включена",
          objective: "Завершите работу, объезжая струи воды",
          alert: true,
        };
  }
  if (missionId === "snow") {
    return completed < 24
      ? {
          key: "snow-main",
          label: "ЭТАП 1 / 2",
          title: "Основной проезд",
          objective: "Расчистите дорогу до развилки",
        }
      : {
          key: "snow-wind",
          label: "НОВЫЙ ВЫЗОВ",
          title: "Ветер замёл боковой проезд",
          objective: "Откройте дополнительный маршрут к ангару",
          alert: true,
        };
  }
  if (missionId === "plant") {
    return completed < 20
      ? {
          key: "plant-main",
          label: "ЭТАП 1 / 2",
          title: "Основное поле",
          objective: "Засадите первые четыре ряда",
        }
      : {
          key: "plant-rough",
          label: "СЛОЖНЫЙ УЧАСТОК",
          title: "Неровный грунт",
          objective: "Снизьте скорость до 15 км/ч и сохраните ровные ряды",
          alert: true,
        };
  }
  if (missionId === "cargo") {
    return completed < 3
      ? {
          key: `cargo-pickup-${completed}`,
          label: `ПОГРУЗКА ${completed + 1} / 3`,
          title: "Логистический терминал",
          objective: "Остановитесь в центре активной платформы",
        }
      : {
          key: "cargo-delivery",
          label: "ФИНАЛЬНЫЙ ЭТАП",
          title: "Тележка загружена",
          objective: "Доставьте контейнеры на склад без резких манёвров",
          alert: true,
        };
  }
  if (missionId === "boat") {
    if (completed < 8)
      return {
        key: "boat-route",
        label: "ЭТАП 1 / 2",
        title: "Подход к рампе",
        objective: "Проведите длинный прицеп через ворота",
      };
    if (completed < 9)
      return {
        key: "boat-turn",
        label: "МАНЁВР",
        title: "Разверните мувер",
        objective: "Развернитесь на площадке перед водой",
        alert: true,
      };
    return {
      key: "boat-reverse",
      label: "ФИНАЛЬНЫЙ ЭТАП",
      title: "Подача задним ходом",
      objective: "Включите задний ход и подайте катер на рампу",
      alert: true,
    };
  }
  if (completed < 5)
    return {
      key: "finale-mow",
      label: "МАШИНА 1 / 3",
      title: "MOW·01",
      objective: "Обработайте демонстрационный сектор",
    };
  if (completed < 9)
    return {
      key: "finale-cargo",
      label: "МАШИНА 2 / 3",
      title: "HAUL·04",
      objective: "Соберите оборудование на платформах",
      alert: true,
    };
  return {
    key: "finale-boat",
    label: "МАШИНА 3 / 3",
    title: "MARINE·05",
    objective: "Завершите смену на водном секторе",
    alert: true,
  };
};

export type ContractRating = {
  stars: 1 | 2 | 3;
  time: 1 | 2 | 3;
  reason: string;
};

export const calculateRating = (
  elapsed: number,
  parTime: number,
): ContractRating => {
  const timeRatio = elapsed / parTime;
  const time = timeRatio <= 1 ? 3 : timeRatio <= 1.35 ? 2 : 1;
  const stars = time;

  let reason = "Норматив выполнен — максимальная оценка";
  if (stars === 2)
    reason = "Контракт выполнен: для трёх звёзд уложитесь в норматив";
  else if (stars === 1)
    reason = "Контракт выполнен: сократите время для следующей звезды";

  return { stars, time, reason };
};

export const calculateStars = (elapsed: number, parTime: number) =>
  calculateRating(elapsed, parTime).stars;

export const mowTargets = Array.from({ length: 48 }, (_, index) => {
  const row = Math.floor(index / 8);
  const col = index % 8;
  return { x: -15.75 + col * 4.5, z: -11.25 + row * 4.5 };
});

export const snowTargets = [
  ...Array.from({ length: 24 }, (_, index) => {
    const t = index / 23;
    return {
      x: -20 + t * 40,
      z: Math.sin(t * Math.PI * 2.15) * 7,
    };
  }),
  ...Array.from({ length: 10 }, (_, index) => {
    const t = index / 9;
    return {
      x: 20 - t * 25,
      z: 10 + t * 15 + Math.sin(t * Math.PI) * 3,
    };
  }),
];

export const plantTargets = Array.from({ length: 40 }, (_, index) => {
  const row = Math.floor(index / 5);
  const cell = index % 5;
  const forward = row % 2 === 0;
  return {
    x: -17.5 + row * 5,
    z: forward ? -15 + cell * 7.5 : 15 - cell * 7.5,
  };
});

export const cargoTargets = [
  { x: -16, z: -13 },
  { x: 5, z: -18 },
  { x: 16, z: 5 },
  { x: -18, z: 19 },
];

export const boatTargets = [
  { x: -16, z: -15 },
  { x: -8, z: -13 },
  { x: 0, z: -8 },
  { x: 6, z: -1 },
  { x: 7, z: 7 },
  { x: 2, z: 14 },
  { x: -7, z: 18 },
  { x: -15, z: 22 },
  { x: -20, z: 27 },
  { x: -20, z: 30 },
];

export const finaleTargets = [
  { x: -18, z: -15 },
  { x: -14, z: -11 },
  { x: -10, z: -7 },
  { x: -6, z: -3 },
  { x: -2, z: 1 },
  { x: 3, z: 4 },
  { x: 10, z: 4 },
  { x: 12, z: 12 },
  { x: 5, z: 16 },
  { x: 0, z: 18 },
  { x: -5, z: 19 },
  { x: -10, z: 21 },
  { x: -15, z: 23 },
  { x: -19, z: 26 },
  { x: -20, z: 29 },
];

export const targetMap = {
  mow: mowTargets,
  snow: snowTargets,
  plant: plantTargets,
  cargo: cargoTargets,
  boat: boatTargets,
  finale: finaleTargets,
} satisfies Record<MissionId, { x: number; z: number }[]>;
